import { defineStore } from 'pinia'
import propertyReportApi from '@/api/propertyreport'
import {
  type AccessUser,
  type DashboardReportSummary,
  type UpdateReportTagsBody,
  type UpdateReportEmailScheduleBody,
  type UpdateReportPortfolioBody,
  type ReportFieldsUpdateResponse,
  type ExpenseBenchmarksResponse,
  type TaxHistoryResponse,
  type PropertyDetailsResponse,
  type PropertyReportSubscriptionAttributes,
  type RecommendationResponse,
  type PricingResponse,
  type PricingSettings,
  type Similarities,
  type CreatePropertyReportSubscriptionAttributes,
  useHider,
  type OwnerDataResponse
} from 'common'
import { useUserStore } from './user'
import { coreClient } from '@/api/coreclient'
import {
  notStarted,
  loading,
  loaded,
  error,
  type Loading,
  isLoaded,
  isLoading,
  isError
} from '@/utils/loading'
import propertyApi from '@/api/property'
import { toDashboardReportSummary } from './propertyReportSummary'

export interface ReportUpdateResult {
  reports: DashboardReportSummary[]
  succeeded: number
  failed: number[]
}

export const usePropertyReportStore = defineStore('propertyreport', {
  state: () => ({
    activeSubscriptions: [] as DashboardReportSummary[],
    activeAdminSubscriptions: [] as PropertyReportSubscriptionAttributes[],
    retrievedReports: false as boolean,
    listRequestGeneration: 0,
    listLoading: false,
    listError: false,
    listAdmin: false,
    listRequestedAdmin: false,

    downloadPdfLoading: false as boolean,

    // this is the report that is currently being viewed
    activeReport: notStarted as Loading<PropertyReportSubscriptionAttributes>,
    similarities: notStarted as Loading<Record<string, Similarities>>,
    expenseBenchmark: notStarted as Loading<ExpenseBenchmarksResponse>,
    subjectTaxHistory: notStarted as Loading<TaxHistoryResponse>,
    revenueManagement: notStarted as Loading<RecommendationResponse>,
    unitPricings: notStarted as Loading<PricingResponse[]>,
    taxAssessorOwnerByProperty: {} as { [key: string]: Loading<OwnerDataResponse> },
    assessorTaxBill: null as { amount: number; year: number | null } | null
  }),
  actions: {
    async refreshList(admin: boolean = false): Promise<boolean> {
      const generation = ++this.listRequestGeneration
      this.listLoading = true
      this.listError = false
      this.listRequestedAdmin = admin
      try {
        const activeSubscriptions = await propertyReportApi.listSummaries(admin)
        if (generation !== this.listRequestGeneration) {
          return false
        }
        this.retrievedReports = true
        this.activeSubscriptions = activeSubscriptions
        this.listAdmin = admin
        const ids = new Set(activeSubscriptions.map((report) => report.id.toString()))
        for (const report of useUserStore().getRecentReports()) {
          if (!ids.has(report.id)) {
            const reportId = Number.parseInt(report.id, 10)
            if (Number.isNaN(reportId)) {
              useUserStore().deleteRecentReport(report.id.toString())
            } else {
              useUserStore().removeReportVisit('property_report', reportId)
            }
          }
        }
        return true
      } catch (err) {
        if (generation !== this.listRequestGeneration) {
          return false
        }
        this.listError = true
        throw err
      } finally {
        if (generation === this.listRequestGeneration) {
          this.listLoading = false
        }
      }
    },
    async updateReportTags(
      updates: (UpdateReportTagsBody & { id: number })[]
    ): Promise<ReportUpdateResult> {
      const succeeded: ReportFieldsUpdateResponse[] = []
      const failed: number[] = []
      // A selection can contain every report in a large organization.
      for (let start = 0; start < updates.length; start += 5) {
        const batch = updates.slice(start, start + 5)
        const outcomes = await Promise.allSettled(
          batch.map(({ id, tags }) => propertyReportApi.updateTags(id, { tags }))
        )
        outcomes.forEach((outcome, index) => {
          if (outcome.status === 'fulfilled') {
            succeeded.push(outcome.value)
          } else {
            failed.push(batch[index]!.id)
          }
        })
      }
      return {
        reports: this.mergeUpdatedReports(succeeded),
        succeeded: succeeded.length,
        failed
      }
    },
    async updateReportEmailSchedule(
      updates: (UpdateReportEmailScheduleBody & { id: number })[]
    ): Promise<ReportUpdateResult> {
      const succeeded: ReportFieldsUpdateResponse[] = []
      const failed: number[] = []
      for (let start = 0; start < updates.length; start += 5) {
        const batch = updates.slice(start, start + 5)
        const outcomes = await Promise.allSettled(
          batch.map(({ id, email_schedule }) =>
            propertyReportApi.updateEmailSchedule(id, { email_schedule })
          )
        )
        outcomes.forEach((outcome, index) => {
          if (outcome.status === 'fulfilled') {
            succeeded.push(outcome.value)
          } else {
            failed.push(batch[index]!.id)
          }
        })
      }
      return {
        reports: this.mergeUpdatedReports(succeeded),
        succeeded: succeeded.length,
        failed
      }
    },
    async updateReportPortfolio(
      updates: (UpdateReportPortfolioBody & { id: number })[]
    ): Promise<ReportUpdateResult> {
      const succeeded: ReportFieldsUpdateResponse[] = []
      const failed: number[] = []
      for (let start = 0; start < updates.length; start += 5) {
        const batch = updates.slice(start, start + 5)
        const outcomes = await Promise.allSettled(
          batch.map(({ id, is_in_portfolio }) =>
            propertyReportApi.updatePortfolio(id, { is_in_portfolio })
          )
        )
        outcomes.forEach((outcome, index) => {
          if (outcome.status === 'fulfilled') {
            succeeded.push(outcome.value)
          } else {
            failed.push(batch[index]!.id)
          }
        })
      }
      return {
        reports: this.mergeUpdatedReports(succeeded),
        succeeded: succeeded.length,
        failed
      }
    },
    mergeUpdatedReports(updates: ReportFieldsUpdateResponse[]): DashboardReportSummary[] {
      const byId = new Map(updates.map((report) => [report.id, report]))
      const updatedReports: DashboardReportSummary[] = []
      const unchangedReports: DashboardReportSummary[] = []
      for (const report of this.activeSubscriptions) {
        const update = byId.get(report.id)
        if (update) {
          updatedReports.push({
            ...report,
            ...update,
            email_schedule: update.email_schedule ?? undefined
          })
        } else {
          unchangedReports.push(report)
        }
      }
      this.activeSubscriptions = updatedReports.concat(unchangedReports)
      return updatedReports
    },
    async copyReport(id: number) {
      const report = await propertyReportApi.getReport(id)
      return this.create(
        {
          ...report,
          name: `${report.name} (Copy)`,
          is_organization_visible: false,
          organization_visibility: null,
          public_id: null
        },
        { throwOnError: true }
      )
    },
    async refreshAdminList() {
      this.activeAdminSubscriptions = await propertyReportApi.list(true)
      this.retrievedReports = true
    },
    removeTagsFromCachedReports(tags: string[]) {
      for (const row of this.activeSubscriptions) {
        row.tags = row.tags.filter((tag) => !tags.includes(tag))
      }
    },
    async getReport(
      id?: number,
      publicId?: string,
      options?: { logVisit?: boolean }
    ): Promise<Loading<PropertyReportSubscriptionAttributes>> {
      this.activeReport = loading
      return await propertyReportApi
        .getReport(id, publicId, options)
        .then((result) => {
          if (options?.logVisit) {
            useUserStore().recordReportVisitOffline({
              report_type: 'property_report',
              report_id: result.id,
              report_name: result.name
            })
          }
          this.activeReport = loaded(result)
          return this.activeReport
        })
        .catch((e) => {
          this.activeReport = error(e)
          return this.activeReport
        })
    },
    async getRevenueManagement(
      subject: PropertyDetailsResponse,
      comparables: PropertyDetailsResponse[],
      parameters: {
        target_occupancy: number
        max_diff_percent: number
        timeframe_days: number
      }
    ): Promise<Loading<RecommendationResponse>> {
      this.revenueManagement = loading
      return propertyApi
        .revenueManagement({
          subject: subject,
          comparables: comparables,
          parameters
        })
        .then((response) => {
          this.revenueManagement = loaded(response)
          return this.revenueManagement
        })
        .catch((err) => {
          console.error(err)
          this.revenueManagement = error(err)
          return this.revenueManagement
        })
    },
    async getOldRevenueManagement(
      subject: PropertyDetailsResponse,
      settings: PricingSettings
    ): Promise<Loading<PricingResponse[]>> {
      this.unitPricings = loading
      return await propertyApi
        .pricing({
          subject,
          settings
        })
        .then((pricing) => {
          this.unitPricings = loaded(pricing)
          return this.unitPricings
        })
        .catch((err) => {
          console.error(err)
          this.unitPricings = error(err)
          return this.unitPricings
        })
    },
    async getExpenseBenchmarks(
      subject: PropertyDetailsResponse,
      customGPR?: number
    ): Promise<Loading<ExpenseBenchmarksResponse>> {
      this.assessorTaxBill = null
      this.expenseBenchmark = loading
      return propertyApi
        .expenseBenchmarks(subject, customGPR)
        .then((benchmarks) => {
          this.expenseBenchmark = loaded(benchmarks)
          this.syncExpensesWithTaxData(subject)
          return this.expenseBenchmark
        })
        .catch((err) => {
          console.error(err)
          this.expenseBenchmark = error(new Error('Failed to get expense benchmarks'))
          return this.expenseBenchmark
        })
    },
    getTaxHistory(subject: PropertyDetailsResponse): Promise<Loading<TaxHistoryResponse>> {
      this.assessorTaxBill = null
      this.subjectTaxHistory = loading
      return propertyApi
        .taxHistory({
          id: subject.id,
          address: subject.street_address,
          city: subject.city,
          state: subject.state,
          zip_code: subject.zip_code,
          lat: subject.lat,
          lon: subject.lon
        })
        .then((taxes) => {
          if (taxes) {
            this.subjectTaxHistory = loaded(taxes)
            this.syncExpensesWithTaxData(subject)
          } else {
            this.subjectTaxHistory = error(new Error('Failed to get taxes'))
          }
          return this.subjectTaxHistory
        })
        .catch((err) => {
          console.error(err)
          this.subjectTaxHistory = error(new Error('Failed to get taxes'))
          return this.subjectTaxHistory
        })
    },
    syncExpensesWithTaxData(subject: PropertyDetailsResponse) {
      if (subject.user_id) {
        // Never swap the tax prediction for properties created in the New Development flow
        return
      }
      const lastYear = new Date().getFullYear() - 1
      if (subject.year_built && subject.year_built >= lastYear) {
        // If the property was just built, the old taxes are going to be wrong
        return
      }
      if (isLoaded(this.subjectTaxHistory) && isLoaded(this.expenseBenchmark)) {
        const taxHistory = this.subjectTaxHistory.data.tax_assessor_history_v2 || []
        let taxBill: { amount: number; year: number | null } | null = null
        if (taxHistory.length) {
          const maxTaxYearEntry = taxHistory.reduce(
            (max, curr) => {
              return curr.assessed_tax_year &&
                curr.assessed_tax_year > (max?.assessed_tax_year ?? -Infinity)
                ? curr
                : max
            },
            null as TaxHistoryResponse['tax_assessor_history_v2'][0] | null
          )
          if (maxTaxYearEntry?.tax_bill_amount) {
            taxBill = {
              amount: maxTaxYearEntry.tax_bill_amount,
              year: maxTaxYearEntry.assessed_tax_year ?? null
            }
          }
        }
        if (!taxBill) {
          return
        }

        const subjectEstimate = this.expenseBenchmark.data.subject_estimate
        const predictedTaxes = subjectEstimate.real_estate_taxes?.predicted
        if (!predictedTaxes) {
          // If we don't have a prediction, let's just keep it that way.
          return
        }
        this.assessorTaxBill = taxBill
        subjectEstimate.real_estate_taxes!.predicted = taxBill.amount
        if (subjectEstimate.total_operating_expenses) {
          subjectEstimate.total_operating_expenses.predicted += taxBill.amount - predictedTaxes
        }
        if (subjectEstimate.noi) {
          subjectEstimate.noi.predicted += predictedTaxes - taxBill.amount
        }
      }
    },
    cleanReportData() {
      this.activeReport = notStarted
      this.similarities = notStarted
      this.expenseBenchmark = notStarted
      this.revenueManagement = notStarted
      this.unitPricings = notStarted
      this.subjectTaxHistory = notStarted
      this.assessorTaxBill = null
    },
    async getByTag(tag: string) {
      if (!this.retrievedReports && !(await this.refreshList())) {
        throw new Error('The report list changed. Please try again.')
      }
      const ids = this.activeSubscriptions
        .filter((report) => report.tags.includes(tag))
        .map((report) => report.id)
      const reports: PropertyReportSubscriptionAttributes[] = []
      for (let start = 0; start < ids.length; start += 100) {
        const batch = ids.slice(start, start + 100)
        const details = new Map(
          (await propertyReportApi.getByIds(batch, true)).map((report) => [report.id, report])
        )
        for (const id of batch) {
          const report = details.get(id)
          if (!report) {
            throw new Error(
              'Some reports could not be loaded. Refresh the dashboard and try again.'
            )
          }
          reports.push(report)
        }
      }
      return reports
    },
    async update(report: PropertyReportSubscriptionAttributes) {
      // Set an empty cron expression on update
      if (report.email_schedule) {
        report.email_schedule.cron_expression ??= ''
      }
      const data = await propertyReportApi.update(report)
      const existingRecordIndex = this.activeSubscriptions.findIndex((_) => _.id === report.id)
      if (existingRecordIndex > -1 && data) {
        this.activeSubscriptions = [toDashboardReportSummary(data)]
          .concat(this.activeSubscriptions.slice(0, existingRecordIndex))
          .concat(this.activeSubscriptions.slice(existingRecordIndex + 1))
      }
      return data
    },
    async delete(id: number) {
      return propertyReportApi.delete(id).then(() => {
        const index = this.activeSubscriptions.findIndex((_) => _.id === id)
        this.activeSubscriptions.splice(index, 1)
        useUserStore().removeReportVisit('property_report', id)
        void useUserStore().refreshPropertyReportLimitUsage()
      })
    },
    async create(
      report: PropertyReportSubscriptionAttributes | CreatePropertyReportSubscriptionAttributes,
      options: { throwOnError?: boolean } = {}
    ) {
      const client = await coreClient()

      let cleanedReport: CreatePropertyReportSubscriptionAttributes
      if ('id' in report) {
        // if the report is a PropertyReportSubscriptionAttributes, we need to remove extra fields
        cleanedReport = useHider(report, [
          'id',
          'created_on',
          'modified_on',
          'deleted_on',
          'feature_flags',
          'access_users',
          'columns',
          'sections',
          'user_id'
        ])
      } else {
        cleanedReport = report
      }

      if (cleanedReport.email_schedule) {
        cleanedReport.email_schedule.cron_expression ??= ''
      }

      return await client.propertyReport
        .create(cleanedReport)
        .then((response) => {
          if (response.data !== null) {
            this.activeSubscriptions = [toDashboardReportSummary(response.data)].concat(
              this.activeSubscriptions
            )
            void useUserStore().refreshPropertyReportLimitUsage()
            return response.data
          }
          return null
        })
        .catch((e) => {
          console.error(e)
          if (options.throwOnError) {
            throw e
          }
          return null
        })
    },
    async sendEmail(id: number) {
      return propertyReportApi.sendEmail(id)
    },
    async getAccess(reportId: number) {
      return propertyReportApi.getAccess(reportId)
    },
    async createAccess(
      reportId: number,
      notify: boolean,
      data: { email: string; user_id: string | null; read_only?: boolean }[]
    ) {
      return propertyReportApi.createAccess(reportId, notify, data)
    },
    async updateAccess(reportId: number, userId: string, readOnly: boolean) {
      return propertyReportApi.updateAccess(reportId, userId, readOnly)
    },
    async deleteAccess(reportId: number, userId: string) {
      return propertyReportApi.deleteAccess(reportId, userId)
    },
    removeReport(reportId: number) {
      const idx = this.activeSubscriptions.findIndex((_) => _.id === reportId)
      this.activeSubscriptions.splice(idx, 1)
    },
    updateAccessUsers(report: PropertyReportSubscriptionAttributes, accessUsers: AccessUser[]) {
      if (this.activeSubscriptions.length === 0) {
        return
      }

      const idx = this.activeSubscriptions.findIndex((_) => {
        return _.id === report.id
      })

      if (idx === -1) {
        return
      }

      const accessUsersExcludingOwner = accessUsers.filter((u) => u.id !== report.user_id)
      this.activeSubscriptions[idx].access_users = accessUsersExcludingOwner
    },
    setDownloadPdfLoading(value: boolean) {
      this.downloadPdfLoading = value
    },
    async fetchTaxOwnerDataForProperty(prop: PropertyDetailsResponse) {
      const client = await coreClient()

      const taxData = this.taxAssessorOwnerByProperty[prop.id]
      if (taxData && (isLoaded(taxData) || isLoading(taxData))) {
        return
      }

      if (!taxData || isError(taxData)) {
        this.taxAssessorOwnerByProperty[prop.id] = loading
      }

      try {
        const data = await client.property.ownerData(
          prop.id,
          prop.street_address,
          prop.city,
          prop.state,
          prop.zip_code,
          prop.lat,
          prop.lon
        )

        if (data) {
          this.taxAssessorOwnerByProperty[prop.id] = loaded(data)
        } else {
          this.taxAssessorOwnerByProperty[prop.id] = error(new Error('No owner data found'))
        }
      } catch (e) {
        console.error(e)
        this.taxAssessorOwnerByProperty[prop.id] = error(new Error('Failed to get owner data'))
      }
    }
  },
  getters: {
    existingReports(state): DashboardReportSummary[] {
      return state.activeSubscriptions
    },
    getDownloadPdfLoading(state) {
      return state.downloadPdfLoading
    }
  }
})
